import type {
  SynchronizationBarangayStatus,
  SynchronizationMonitoringResponse,
} from "@rbim/shared";
import { AlertTriangle, Ban, CheckCircle2, Clock3, Copy, KeyRound, Laptop2, Plus, RefreshCw, ShieldOff, Wifi, WifiOff } from "lucide-react";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ApiRequestError, apiRequest } from "../api";
import { useAuth } from "../auth";

type SyncDevice = {
  id: string;
  barangayId: string;
  barangayName?: string;
  deviceName: string;
  status: "ACTIVE" | "SUSPENDED" | "REVOKED";
  enrolledAt?: string;
  pairedAt?: string | null;
  lastSeenAt?: string | null;
};

type DeletionRequest = {
  id: string;
  targetType: "HOUSEHOLD" | "INDIVIDUAL";
  recordId: string;
  memberIndex: number | null;
  targetLabel: string;
  reason: string;
  status: "PENDING" | "OTP_ISSUED" | "CONSUMED" | "EXPIRED" | "CANCELLED" | "STALE";
  otpExpiresAt: string | null;
  otpIssuedAt: string | null;
  createdAt: string;
  requestedByName: string;
  barangayName: string;
};

export function SynchronizationPage() {
  const { user } = useAuth();
  const [monitoring, setMonitoring] = useState<SynchronizationMonitoringResponse | null>(null);
  const [devices, setDevices] = useState<SyncDevice[]>([]);
  const [deletionRequests, setDeletionRequests] = useState<DeletionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deviceMessage, setDeviceMessage] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [barangayId, setBarangayId] = useState("");
  const [creatingDevice, setCreatingDevice] = useState(false);
  const [updatingDeviceId, setUpdatingDeviceId] = useState("");
  const [issuingDeletionRequestId, setIssuingDeletionRequestId] = useState("");
  const [pendingOtpRequest, setPendingOtpRequest] = useState<DeletionRequest | null>(null);
  const [deletionOtp, setDeletionOtp] = useState<{ requestId: string; otp: string; expiresAt: string } | null>(null);
  const [deletionMessage, setDeletionMessage] = useState("");
  const mayEnrollDevices = user?.role === "MUNICIPAL_CITY_ADMINISTRATOR";
  const mayManageDeletionOtp = user?.role === "MUNICIPAL_CITY_ADMINISTRATOR";

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    const monitoringRequest = apiRequest<SynchronizationMonitoringResponse>("/monitoring/synchronization");
    const deviceRequest = mayEnrollDevices
      ? apiRequest<{ devices: SyncDevice[] }>("/admin/sync-devices")
      : Promise.resolve({ devices: [] as SyncDevice[] });
    const deletionRequest = mayManageDeletionOtp
      ? apiRequest<{ requests: DeletionRequest[] }>("/admin/deletion-requests")
      : Promise.resolve({ requests: [] as DeletionRequest[] });

    Promise.all([monitoringRequest, deviceRequest, deletionRequest])
      .then(([monitoringResponse, deviceResponse, deletionResponse]) => {
        setMonitoring(monitoringResponse);
        setDevices(deviceResponse.devices);
        setDeletionRequests(deletionResponse.requests);
        setBarangayId((current) => current || monitoringResponse.barangays[0]?.barangayId || "");
      })
      .catch((requestError: unknown) => {
        setError(requestError instanceof ApiRequestError ? requestError.message : "Synchronization activity could not be loaded.");
      })
      .finally(() => setLoading(false));
  }, [mayEnrollDevices, mayManageDeletionOtp]);

  useEffect(() => { load(); }, [load]);

  async function preEnrollDevice(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!mayEnrollDevices || !barangayId || !deviceName.trim()) return;
    setCreatingDevice(true);
    setDeviceMessage("");
    setPairingCode("");
    try {
      const result = await apiRequest<{ device: SyncDevice; pairingCode: string }>("/admin/sync-devices", {
        method: "POST",
        body: JSON.stringify({ barangayId, deviceName: deviceName.trim() }),
      });
      setPairingCode(result.pairingCode);
      setDeviceMessage("Workstation pre-enrolled. Copy this one-time pairing code now; it will not be shown again and is consumed after successful pairing.");
      setDeviceName("");
      const refreshed = await apiRequest<{ devices: SyncDevice[] }>("/admin/sync-devices");
      setDevices(refreshed.devices);
    } catch (requestError) {
      setDeviceMessage(requestError instanceof ApiRequestError ? requestError.message : "The workstation could not be pre-enrolled.");
    } finally {
      setCreatingDevice(false);
    }
  }

  async function updateDeviceStatus(device: SyncDevice, status: "SUSPENDED" | "REVOKED") {
    const action = status === "REVOKED" ? "revoke" : "suspend";
    const detail = status === "REVOKED"
      ? "This permanently revokes its current pairing. A replacement must be pre-enrolled before it can synchronize again."
      : "This immediately revokes its active sessions and credentials. Revoke and pre-enroll a replacement when access should be restored.";
    if (!window.confirm(`${action[0]?.toUpperCase()}${action.slice(1)} ${device.deviceName}? ${detail}`)) return;

    setUpdatingDeviceId(device.id);
    setDeviceMessage("");
    setPairingCode("");
    try {
      await apiRequest(`/admin/sync-devices/${device.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      const refreshed = await apiRequest<{ devices: SyncDevice[] }>("/admin/sync-devices");
      setDevices(refreshed.devices);
      setDeviceMessage(`${device.deviceName} is now ${status.toLowerCase()}. Existing synchronization sessions and credentials were revoked.`);
    } catch (requestError) {
      setDeviceMessage(requestError instanceof ApiRequestError ? requestError.message : "The workstation status could not be changed.");
    } finally {
      setUpdatingDeviceId("");
    }
  }

  async function issueDeletionOtp(deletionRequest: DeletionRequest) {
    setIssuingDeletionRequestId(deletionRequest.id);
    setDeletionOtp(null);
    setDeletionMessage("");
    try {
      const result = await apiRequest<{ requestId: string; otp: string; expiresAt: string; message: string }>(`/admin/deletion-requests/${deletionRequest.id}/otp`, { method: "POST" });
      setDeletionOtp({ requestId: result.requestId, otp: result.otp, expiresAt: result.expiresAt });
      setDeletionMessage(result.message);
      setPendingOtpRequest(null);
      const refreshed = await apiRequest<{ requests: DeletionRequest[] }>("/admin/deletion-requests");
      setDeletionRequests(refreshed.requests);
    } catch (requestError) {
      setDeletionMessage(requestError instanceof ApiRequestError ? requestError.message : "The deletion code could not be generated.");
    } finally {
      setIssuingDeletionRequestId("");
    }
  }

  async function copyPairingCode() {
    if (!pairingCode) return;
    try {
      await navigator.clipboard.writeText(pairingCode);
      setDeviceMessage("One-time pairing code copied. Enter it on the approved RBIM Desktop workstation.");
    } catch {
      setDeviceMessage("Copy was unavailable. Select the pairing code manually and enter it on the approved workstation.");
    }
  }

  async function copyDeletionOtp() {
    if (!deletionOtp) return;
    try {
      await navigator.clipboard.writeText(deletionOtp.otp);
      setDeletionMessage("Deletion code copied. Share it only with the Barangay Supervisor who submitted this exact request.");
    } catch {
      setDeletionMessage("Copy was unavailable. Select the code manually.");
    }
  }

  return (
    <section className="portal-page">
      <header className="page-heading">
        <div><span className="eyebrow">Municipality operations</span><h1>Synchronization Monitoring</h1><p>Monitor synchronization health and manage municipality-authorized one-time actions.</p></div>
        <button type="button" className="secondary-button" onClick={load} disabled={loading}><RefreshCw />Refresh</button>
      </header>

      {error ? <div className="form-error" role="alert">{error}</div> : null}
      <div className="notice-card"><strong><Wifi />Synchronization telemetry only</strong><p>This section reports operation freshness and accepted/failed synchronization counts. Household and resident details are not exposed in synchronization telemetry.</p></div>

      <section className="dashboard-metrics report-metrics" aria-label="Synchronization summary">
        <SyncMetric label="Barangays in scope" value={monitoring?.totals.barangays ?? null} loading={loading} />
        <SyncMetric label="Active in last 24 hours" value={monitoring?.totals.activeLast24Hours ?? null} loading={loading} />
        <SyncMetric label="Stale / no activity" value={monitoring ? monitoring.totals.stale + monitoring.totals.noActivity : null} loading={loading} attention={Boolean(monitoring && monitoring.totals.stale + monitoring.totals.noActivity > 0)} />
        <SyncMetric label="Failed operations" value={monitoring?.totals.failedOperations ?? null} loading={loading} attention={Boolean(monitoring && monitoring.totals.failedOperations > 0)} />
      </section>

      {mayManageDeletionOtp ? (
        <section className="form-card">
          <div className="section-heading">
            <h2><KeyRound /> MPO Deletion OTP Management</h2>
            <p>Generate a time-limited, single-use code only after reviewing the Barangay Supervisor's deletion request and reason. Each code is bound to one exact record and request.</p>
          </div>
          {pendingOtpRequest ? (
            <div className="otp-confirmation-card" role="dialog" aria-labelledby="otp-confirmation-title">
              <div className="otp-confirmation-heading">
                <div className="otp-confirmation-icon"><KeyRound /></div>
                <div>
                  <span className="eyebrow">Deletion authorization</span>
                  <h3 id="otp-confirmation-title">Generate one-time deletion code?</h3>
                  <p>Review this request carefully before issuing a code. The code will be valid for 10 minutes and can be used only once.</p>
                </div>
              </div>
              <div className="otp-confirmation-grid">
                <div><span>Target</span><strong>{pendingOtpRequest.targetLabel}</strong></div>
                <div><span>Barangay</span><strong>{pendingOtpRequest.barangayName}</strong></div>
                <div><span>Requested by</span><strong>{pendingOtpRequest.requestedByName}</strong></div>
                <div><span>Request type</span><strong>{pendingOtpRequest.targetType === "HOUSEHOLD" ? "Household" : "Individual"}</strong></div>
              </div>
              <div className="otp-confirmation-reason">
                <span>Deletion reason</span>
                <p>{pendingOtpRequest.reason}</p>
              </div>
              <div className="otp-confirmation-actions">
                <button type="button" className="secondary-button" disabled={Boolean(issuingDeletionRequestId)} onClick={() => setPendingOtpRequest(null)}>Cancel</button>
                <button type="button" className="primary-button" disabled={Boolean(issuingDeletionRequestId)} onClick={() => void issueDeletionOtp(pendingOtpRequest)}>
                  <KeyRound />{issuingDeletionRequestId === pendingOtpRequest.id ? "Generating…" : "Confirm & Generate OTP"}
                </button>
              </div>
            </div>
          ) : null}
          {deletionOtp ? (
            <div className="notice-card">
              <strong>One-time Deletion Code</strong>
              <p><code>{deletionOtp.otp}</code></p>
              <p>Request: <code>{deletionOtp.requestId}</code></p>
              <p>Expires: {formatDateTime(deletionOtp.expiresAt)}</p>
              <p>This code is not stored or logged in plaintext and becomes invalid after successful use.</p>
              <button type="button" className="secondary-button" onClick={() => void copyDeletionOtp()}><Copy />Copy Deletion Code</button>
            </div>
          ) : null}
          {deletionMessage ? <div role="status" className="notice-card"><p>{deletionMessage}</p></div> : null}
          <div className="responsive-table">
            <table>
              <thead><tr><th>Requested by</th><th>Barangay</th><th>Target</th><th>Reason</th><th>Status</th><th>Requested</th><th>Authorization</th></tr></thead>
              <tbody>
                {deletionRequests.length ? deletionRequests.map((item) => <tr key={item.id}>
                  <td><strong>{item.requestedByName}</strong></td>
                  <td>{item.barangayName}</td>
                  <td><strong>{item.targetLabel}</strong><br /><span className="muted-copy">{item.targetType === "HOUSEHOLD" ? "Household" : "Individual"}</span></td>
                  <td>{item.reason}</td>
                  <td><span className={`badge ${item.status === "OTP_ISSUED" ? "available" : ""}`}>{item.status.replaceAll("_", " ")}</span>{item.otpExpiresAt ? <><br /><span className="muted-copy">Expires {formatDateTime(item.otpExpiresAt)}</span></> : null}</td>
                  <td>{formatDateTime(item.createdAt)}</td>
                  <td>{item.status === "PENDING" || item.status === "OTP_ISSUED" ? <button type="button" className="primary-button" disabled={issuingDeletionRequestId === item.id} onClick={() => { setPendingOtpRequest(item); setDeletionMessage(""); }}><KeyRound />{issuingDeletionRequestId === item.id ? "Generating…" : item.status === "OTP_ISSUED" ? "Reissue OTP" : "Generate OTP"}</button> : "—"}</td>
                </tr>) : <tr><td colSpan={7}>No deletion authorization requests have been submitted.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {mayEnrollDevices ? (
        <section className="form-card">
          <div className="section-heading">
            <h2><Laptop2 />Pre-enroll a Desktop workstation</h2>
            <p>Only administrator-approved workstations may pair with the production synchronization server. Encoder credentials alone cannot create a trusted workstation.</p>
          </div>
          <form onSubmit={preEnrollDevice} className="user-account-assignment-grid">
            <label>Barangay<select required value={barangayId} onChange={(event) => setBarangayId(event.currentTarget.value)}><option value="">Select Barangay</option>{monitoring?.barangays.map((barangay) => <option key={barangay.barangayId} value={barangay.barangayId}>{barangay.barangayName}</option>)}</select></label>
            <label>Workstation name<input required minLength={2} maxLength={160} value={deviceName} onChange={(event) => setDeviceName(event.currentTarget.value)} placeholder="e.g. Baroy - Poblacion Encoder 1" /></label>
            <button type="submit" className="primary-button" disabled={creatingDevice || !barangayId || !deviceName.trim()}><Plus />{creatingDevice ? "Pre-enrolling…" : "Pre-enroll workstation"}</button>
          </form>
          {pairingCode ? (
            <div className="notice-card">
              <strong>One-time Pairing Code</strong>
              <p><code>{pairingCode}</code></p>
              <p>This code is shown only for this enrollment response and is invalid after successful pairing.</p>
              <button type="button" className="secondary-button" onClick={() => void copyPairingCode()}><Copy />Copy Pairing Code</button>
            </div>
          ) : null}
          {deviceMessage ? <div role="status" className="notice-card"><p>{deviceMessage}</p></div> : null}
          <div className="responsive-table">
            <table>
              <thead><tr><th>Workstation</th><th>Barangay</th><th>Device ID</th><th>Pairing</th><th>Status</th><th>Last seen</th><th>Access action</th></tr></thead>
              <tbody>
                {devices.length ? devices.map((device) => <tr key={device.id}>
                  <td><strong>{device.deviceName}</strong></td>
                  <td>{device.barangayName ?? "—"}</td>
                  <td><code>{device.id}</code></td>
                  <td>{device.pairedAt ? "Paired" : "Awaiting first pairing"}</td>
                  <td><span className={`badge ${device.status === "ACTIVE" ? "available" : ""}`}>{device.status}</span></td>
                  <td>{formatDateTime(device.lastSeenAt ?? null)}</td>
                  <td>
                    <div className="record-action-row">
                      {device.status === "ACTIVE" ? <button type="button" className="secondary-button" disabled={updatingDeviceId === device.id} onClick={() => void updateDeviceStatus(device, "SUSPENDED")}><ShieldOff />Suspend</button> : null}
                      {device.status !== "REVOKED" ? <button type="button" className="danger-button" disabled={updatingDeviceId === device.id} onClick={() => void updateDeviceStatus(device, "REVOKED")}><Ban />Revoke</button> : null}
                    </div>
                  </td>
                </tr>) : <tr><td colSpan={7}>No pre-enrolled workstations yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="table-card">
        <div className="table-title"><Wifi /><div><h2>Barangay synchronization activity</h2><p>{monitoring?.locationLabel ?? "Authorized Municipality/City scope"}</p></div></div>
        <div className="responsive-table">
          <table>
            <thead><tr><th>Barangay</th><th>Last successful sync</th><th>Last failed sync</th><th>Status</th><th>Operations</th></tr></thead>
            <tbody>
              {monitoring?.barangays.length ? monitoring.barangays.map((barangay) => (
                <tr key={barangay.barangayId}>
                  <td><strong>{barangay.barangayName}</strong></td>
                  <td>{formatDateTime(barangay.lastSuccessfulSyncAt)}</td>
                  <td>{formatDateTime(barangay.lastFailedSyncAt)}</td>
                  <td><SyncStatus status={barangay.status} /></td>
                  <td>{barangay.acceptedOperations.toLocaleString("en-PH")} accepted / {barangay.failedOperations.toLocaleString("en-PH")} failed</td>
                </tr>
              )) : <tr><td colSpan={5}>{loading ? "Loading synchronization activity…" : "No authorized Barangays are available."}</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}

function SyncMetric({ label, value, loading, attention = false }: { label: string; value: number | null; loading: boolean; attention?: boolean }) {
  return <article className="dashboard-metric-card"><span className="dashboard-metric-icon">{attention ? <AlertTriangle /> : <CheckCircle2 />}</span><div><span>{label}</span><strong>{loading || value === null ? "…" : value.toLocaleString("en-PH")}</strong></div></article>;
}

function SyncStatus({ status }: { status: SynchronizationBarangayStatus["status"] }) {
  if (status === "ACTIVE_LAST_24H") return <span className="badge available"><CheckCircle2 />Active &lt;24h</span>;
  if (status === "STALE") return <span className="badge"><Clock3 />Stale</span>;
  return <span className="badge"><WifiOff />No activity</span>;
}

function formatDateTime(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" });
}
